/**
 * Route Configuration
 */
import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { AdminLayout } from '@presentation/layouts'
import { Loading } from '@presentation/components'
import { DevAdminGuard } from '@presentation/components/auth'
import { ROUTES } from '@shared/constants'

// Lazy load pages
const DashboardPage = lazy(() => import('@features/dashboard/DashboardPage'))
const HealthPage = lazy(() => import('@features/health/HealthPage'))
const WorkflowListPage = lazy(() => import('@features/workflows/WorkflowListPage'))
const WorkflowDetailsPage = lazy(() => import('@features/workflows/WorkflowDetailsPage'))
const ExecutionListPage = lazy(() => import('@features/executions/ExecutionListPage'))
const ExecutionDetailsPage = lazy(() => import('@features/executions/ExecutionDetailsPage'))
const MonitoringPage = lazy(() => import('@features/monitoring/MonitoringPage'))
const TemplateListPage = lazy(() => import('@features/templates/TemplateListPage'))
const TemplateDetailsPage = lazy(() => import('@features/templates/TemplateDetailsPage'))
const TeamListPage = lazy(() => import('@features/teams/TeamListPage'))
const TeamDetailsPage = lazy(() => import('@features/teams/TeamDetailsPage'))
const TenantListPage = lazy(() => import('@features/tenants/TenantListPage'))
const UserListPage = lazy(() => import('@features/users/UserListPage'))
const ConnectorListPage = lazy(() => import('@features/connectors/ConnectorListPage'))
const ConnectorDetailsPage = lazy(() => import('@features/connectors/ConnectorDetailsPage'))
const InfraOverviewPage = lazy(() => import('@features/infra/InfraOverviewPage'))
const RedisPage = lazy(() => import('@features/infra/RedisPage'))
const ServiceBusPage = lazy(() => import('@features/infra/ServiceBusPage'))
const DbBrowserPage = lazy(() => import('@features/infra/DbBrowserPage'))
const DbTablePage = lazy(() => import('@features/infra/DbTablePage'))
const DeploymentPage = lazy(() => import('@features/deployment/DeploymentPage'))
const AuditLogPage = lazy(() => import('@features/audit/AuditLogPage'))
const PresetListPage = lazy(() => import('@features/presentation/PresetListPage'))
const PresetEditorPage = lazy(() => import('@features/presentation/PresetEditorPage'))
const WhatsNewPage = lazy(() => import('@presentation/pages/WhatsNewPage'))
const NotFoundPage = lazy(() => import('@presentation/pages/NotFoundPage'))
const LoginPage = lazy(() => import('@presentation/pages/auth/LoginPage'))
const AuthCallbackPage = lazy(() => import('@presentation/pages/auth/AuthCallbackPage'))

function withSuspense(Component: React.ComponentType) {
  return (
    <Suspense fallback={<Loading fullPage tip="Loading..." />}>
      <Component />
    </Suspense>
  )
}

const routes: RouteObject[] = [
  // Auth callback — accessible regardless of auth state
  {
    path: ROUTES.AUTH_CALLBACK,
    element: withSuspense(AuthCallbackPage),
  },
  // Login — redirects to gateway
  {
    path: ROUTES.LOGIN,
    element: withSuspense(LoginPage),
  },
  // Protected routes — require isDevAdmin
  {
    path: '/',
    element: <DevAdminGuard />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.DASHBOARD} replace />,
          },
          {
            path: 'dashboard',
            element: withSuspense(DashboardPage),
          },
          {
            path: 'audit',
            element: withSuspense(AuditLogPage),
          },
          {
            path: 'presentation',
            element: withSuspense(PresetListPage),
          },
          {
            path: 'presentation/new',
            element: withSuspense(PresetEditorPage),
          },
          {
            path: 'presentation/:key',
            element: withSuspense(PresetEditorPage),
          },
          {
            path: 'health',
            element: withSuspense(HealthPage),
          },
          {
            path: 'workflows',
            element: withSuspense(WorkflowListPage),
          },
          {
            path: 'workflows/:id',
            element: withSuspense(WorkflowDetailsPage),
          },
          {
            path: 'executions',
            element: withSuspense(ExecutionListPage),
          },
          {
            path: 'executions/:id',
            element: withSuspense(ExecutionDetailsPage),
          },
          {
            path: 'monitoring',
            element: withSuspense(MonitoringPage),
          },
          {
            path: 'deployment',
            element: withSuspense(DeploymentPage),
          },
          {
            path: 'connectors',
            element: withSuspense(ConnectorListPage),
          },
          {
            path: 'connectors/:id',
            element: withSuspense(ConnectorDetailsPage),
          },
          {
            path: 'teams',
            element: withSuspense(TeamListPage),
          },
          {
            path: 'teams/:id',
            element: withSuspense(TeamDetailsPage),
          },
          {
            path: 'tenants',
            element: withSuspense(TenantListPage),
          },
          {
            path: 'users',
            element: withSuspense(UserListPage),
          },
          {
            path: 'invitations',
            element: <Navigate to="/users" replace />,
          },
          {
            path: 'templates',
            element: withSuspense(TemplateListPage),
          },
          {
            path: 'templates/:id',
            element: withSuspense(TemplateDetailsPage),
          },
          {
            path: 'infra',
            element: withSuspense(InfraOverviewPage),
          },
          {
            path: 'infra/redis',
            element: withSuspense(RedisPage),
          },
          {
            path: 'infra/service-bus',
            element: withSuspense(ServiceBusPage),
          },
          {
            path: 'infra/db/:schema',
            element: withSuspense(DbBrowserPage),
          },
          {
            path: 'infra/db/:schema/:table',
            element: withSuspense(DbTablePage),
          },
          {
            path: 'whats-new',
            element: withSuspense(WhatsNewPage),
          },
        ],
      },
    ],
  },
  // 404
  {
    path: ROUTES.NOT_FOUND,
    element: withSuspense(NotFoundPage),
  },
  // Catch-all
  {
    path: '*',
    element: <Navigate to={ROUTES.NOT_FOUND} replace />,
  },
]

export const router = createBrowserRouter(routes)
export { routes }
